import React from 'react';
import Icon from '../../../components/AppIcon';

const RecentActivityFeed = ({ activities = [] }) => {
  const getActivityIcon = (activityType) => {
    switch (activityType) {
      case 'CASE_OPENED':
        return 'FolderOpen';
      case 'CASE_CLOSED': case'CASE_COMPLETED':
        return 'CheckCircle';
      case 'ADDRESS_ADDED':
        return 'MapPin';
      case 'ANALYSIS_COMPLETED':
        return 'BarChart3';
      case 'REPORT_GENERATED':
        return 'FileText';
      case 'CASE_UPDATED':
        return 'Edit';
      default:
        return 'Activity';
    }
  };

  const getActivityColor = (activityType) => {
    switch (activityType) {
      case 'CASE_OPENED':
        return 'text-primary';
      case 'CASE_CLOSED': case'CASE_COMPLETED':
        return 'text-success';
      case 'ADDRESS_ADDED':
        return 'text-warning';
      case 'ANALYSIS_COMPLETED':
        return 'text-accent';
      case 'REPORT_GENERATED':
        return 'text-primary';
      case 'CASE_UPDATED':
        return 'text-text-secondary';
      default:
        return 'text-text-secondary';
    }
  };

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date?.toLocaleDateString();
  };

  if (activities?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg shadow-forensic-card p-6">
        <div className="flex items-center space-x-2 mb-6">
          <Icon name="Activity" size={20} color="var(--color-primary)" />
          <h3 className="text-lg font-semibold text-text-primary">Recent Activity</h3>
        </div>
        
        <div className="text-center py-8">
          <Icon name="Clock" size={40} color="var(--color-text-secondary)" className="mx-auto mb-3" />
          <p className="text-text-secondary text-sm">No recent activity</p>
          <p className="text-text-secondary text-xs mt-1">Activity will appear here as investigations progress</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg shadow-forensic-card">
      <div className="p-6 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="Activity" size={20} color="var(--color-primary)" />
          <h3 className="text-lg font-semibold text-text-primary">Recent Activity</h3>
        </div>
      </div>
      <div className="p-6">
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {activities?.slice(0, 15)?.map((activity, index) => (
            <div key={activity?.id || index} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full bg-muted ${getActivityColor(activity?.activity_type)}`}>
                <Icon 
                  name={getActivityIcon(activity?.activity_type)} 
                  size={14} 
                  color="currentColor"
                />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-text-primary truncate">
                    {activity?.description}
                  </p>
                  <span className="text-xs text-text-secondary ml-2 flex-shrink-0">
                    {formatRelativeTime(activity?.created_at)}
                  </span>
                </div>
                
                {activity?.user?.full_name && (
                  <p className="text-xs text-text-secondary mt-1">
                    by {activity?.user?.full_name}
                  </p>
                )}
                
                {activity?.metadata && Object.keys(activity?.metadata)?.length > 0 && (
                  <div className="mt-2">
                    {activity?.metadata?.case_number && (
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-primary/10 text-primary">
                        {activity?.metadata?.case_number}
                      </span>
                    )}
                    {activity?.metadata?.address && (
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-warning/10 text-warning ml-2 font-mono">
                        {activity?.metadata?.address?.substring(0, 12)}...
                      </span>
                    )}
                    {activity?.metadata?.risk_score && (
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-error/10 text-error ml-2">
                        Risk: {activity?.metadata?.risk_score}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        
        {activities?.length > 15 && (
          <div className="mt-4 pt-4 border-t border-border text-center">
            <button className="text-sm text-primary hover:text-primary/80 transition-colors">
              View all activity
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentActivityFeed;